/**
 * Статус генерации для интерфейса (§68).
 *
 * Опрос адаптивный: интервал растёт с возрастом задачи. Создание аватара идёт
 * часами, и держать секундный опрос всё это время незачем
 * (agents/api-jobs-queues.md §8). SSE заложен в контракт и включится позже
 * без изменения этого хука.
 */
import { useQuery } from '@tanstack/react-query';
import { pollIntervalMs, type JobView } from '@alterium/shared';
import { jobsApi } from '../../api/jobs';
import { i18n } from '../../i18n';

export function useJobStatus(jobId: string | null) {
  return useQuery({
    queryKey: ['job', jobId],
    queryFn: () => jobsApi.get(jobId as string),
    enabled: jobId !== null,
    staleTime: 0,
    refetchInterval: (query) => {
      const job = query.state.data;
      return job ? pollIntervalMs(job) : 2000;
    },
    /**
     * На скрытой вкладке опрос приостанавливается — это правильно, генерация
     * идёт на сервере и незачем её сторожить. Но при возврате состояние надо
     * обновить сразу: пользователь уходит и приходит именно тогда, когда
     * ждёт результат (agents/web-features.md §13). Глобально refetchOnWindowFocus
     * выключен, поэтому здесь включаем точечно.
     */
    refetchOnWindowFocus: true,
  });
}

export function useActiveJobs() {
  return useQuery({
    queryKey: ['jobs', 'active'],
    queryFn: () => jobsApi.list({ activeOnly: true }),
    staleTime: 0,
    refetchInterval: (query) => (query.state.data?.data.length ? 3000 : 15_000),
    refetchOnWindowFocus: true,
  });
}

/** Текст для карточки: три этапа §68 либо результат. */
export function jobStatusLabel(job: JobView): string {
  if (job.stageLabel) return job.stageLabel;
  if (job.status === 'completed') return i18n.t('job.completed');
  if (job.status === 'cancelled') return i18n.t('job.cancelled');
  return i18n.t('job.failed');
}
